import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Flex,
  IconButton,
  Text
} from '@sparrowengg/twigs-react';
import {
  ChevronRightIcon,
  DeleteIcon,
  PlusIcon
} from '@sparrowengg/twigs-react-icons';
import ActionEnergy from 'commons/icons/action-energy';
import UserShield from 'commons/icons/user-shield';
import React, { useEffect, useState } from 'react';
import { SidebarItemProps, SidebarProps } from '../types';
import { useSelector } from 'react-redux';
import { sidebarItems } from '../constants';
import { IRootState } from 'commons/types';

const SideBar = ({
  handleSidebarItem,
  eventFields,
  handleCurrentField,
  removeEventFields,
  currentField,
  events,
  eventHandlerRef,
  iparamsRef,
  oauthRef
}: SidebarProps) => {
  return (
    <Box
      css={{
        minWidth: 280,
        borderRight: '$borderWidths$xs solid $white200',
        height: 'calc(100vh - 73px)',
        padding: '$8',
        overflowY: "auto"
      }}
    >
      <Text size="md" className="explorer">
        Explorer
      </Text>
      <Flex flexDirection="column" css={{ marginTop: '$6' }}>
        <SidebarItem
          type={sidebarItems.CONFIGURATION}
          heading="Configurations"
          icon={<UserShield color={'rgba(255, 255, 255, 0.7)'} size={16} />}
          handleSidebarItem={handleSidebarItem}
          handleCurrentField={handleCurrentField}
          eventFields={eventFields}
          currentField={currentField}
          eventHandlerRef={eventHandlerRef}
          iparamsRef={iparamsRef}
          oauthRef={oauthRef}
          fields={[
            { label: 'IParams', value: 'IPARAMS' },
            { label: 'OAuth', value: 'OAUTH' }
          ]}
        />
        <SidebarItem
          eventFields={eventFields}
          handleSidebarItem={handleSidebarItem}
          handleCurrentField={handleCurrentField}
          type={sidebarItems.EVENT_HANDLER}
          heading="Event Handlers"
          events={events}
          isEventHandler
          icon={<ActionEnergy color={'rgba(255, 255, 255, 0.7)'} size={16} />}
          removeEventFields={removeEventFields}
          currentField={currentField}
          fields={eventFields.map((field) => ({
            label: field.field ?? '',
            value: field.field ?? ''
          }))}
          eventHandlerRef={eventHandlerRef}
        />
      </Flex>
    </Box>
  );
};

export default SideBar;

const SidebarItem = ({
  heading,
  fields,
  icon,
  type,
  handleSidebarItem,
  handleCurrentField,
  eventFields,
  removeEventFields,
  currentField,
  events,
  isEventHandler,
  eventHandlerRef,
  iparamsRef,
  oauthRef
}: SidebarItemProps) => {
  const [isActive, setIsActive] = useState(false);
  const { isEventOpen } = useSelector(
    (state: IRootState) => state.tooltipValues
  );

  useEffect(() => {
    if (isEventOpen) {
      setIsActive(true);
    }
  }, [isEventOpen]);

  return (
    <Flex flexDirection="column">
      <Flex
        justifyContent="space-between"
        css={{
          padding: '$4',
          cursor: 'pointer',
          background: isActive ? '$white300' : 'transparent',
          borderRadius: '$lg'
        }}
        onClick={() => setIsActive(!isActive)}
        ref={isEventHandler ? eventHandlerRef : null}
      >
        <Flex alignItems="center">
          {icon}
          <Text css={{ marginInline: '$4 $2' }} size="sm">
            {heading}
          </Text>
          {isEventHandler ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <IconButton icon={<PlusIcon />} variant="ghost" color="light" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                css={{
                  background: '#151515',
                  border: '0.5px solid $white300',
                  minWidth: 248,
                  position: 'relative',
                  left: '-$8',
                  cursor: 'pointer'
                }}
                sideOffset={14}
                align="center"
              >
                <Text
                  css={{
                    textTransform: 'uppercase',
                    color: '$white700',
                    padding: '$4 $6'
                  }}
                  size="xs"
                  weight="bold"
                >
                  Choose Event groups
                </Text>
                {events?.map((event) => (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      css={{
                        background: '#151515 !important',
                        color: '$white900 !important'
                      }}
                    >
                      {event?.name}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent
                      sideOffset={4}
                      alignOffset={-5}
                      css={{
                        background: '#151515',
                        border: '0.5px solid $white300'
                      }}
                    >
                      <Text
                        css={{
                          textTransform: 'uppercase',
                          color: '$white700',
                          padding: '$4 $6'
                        }}
                        size="xs"
                        weight="bold"
                      >
                        Choose Events
                      </Text>
                      {event?.events?.map((currentEvent) => {
                        const isDisabled = eventFields.some(
                          (field) => field.field === currentEvent.name
                        ); //applying styles explicitly since the dropdown has different behaviour in dark mode
                        return (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isDisabled) {
                                handleSidebarItem(
                                  sidebarItems.EVENT_HANDLER,
                                  currentEvent.name
                                );
                                setIsActive(true);
                              }
                            }}
                            css={{
                              color: '$white900 !important',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              opacity: isDisabled ? 0.3 : 1,
                              pointerEvents: isDisabled ? 'none' : 'all',
                              '&:hover': {
                                background: '$white100'
                              }
                            }}
                          >
                            <Flex alignItems="center" gap="$2">
                              <ActionEnergy />
                              {currentEvent?.name}
                            </Flex>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </Flex>
        <Box
          css={{
            transform: `rotate(${isActive ? '90deg' : '0deg'})`,
            transition: 'all .3s ease'
          }}
        >
          <ChevronRightIcon size={24} color="rgba(255, 255, 255, 0.5)" />
        </Box>
      </Flex>
      {fields?.length ? (
        <Flex
          flexDirection="column"
          css={{
            height: isActive ? '100%' : 0,
            overflow: 'auto',
            marginBlock: isActive ? '$4' : 0
          }}
        >
          {fields?.map((field) => {
            const isActive = field.value === currentField.field;
            let currentRef = null;
            if (type === sidebarItems.CONFIGURATION) {
              if (field.value === 'IPARAMS') {
                currentRef = iparamsRef;
              } else {
                currentRef = oauthRef;
              }
            }
            return (
              <Flex
                justifyContent="space-between"
                ref={currentRef}
                onClick={() => {
                  handleCurrentField(
                    type === sidebarItems.CONFIGURATION
                      ? sidebarItems.CONFIGURATION
                      : sidebarItems.EVENT_HANDLER,
                    field.value
                  );
                }}
                css={{
                  padding: '$4',
                  cursor: 'pointer',
                  borderRadius: '$lg',
                  background: isActive
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'transparent',
                  color: isActive ? '$primary400 !important ' : '$white700',
                  '&:hover': {
                    background: '$white100',
                    '.delete-btn': {
                      opacity: 1
                    }
                  },
                  ...(isActive && {
                    '& path': { stroke: '$primary400 !important' }
                  })
                }}
              >
                <Flex alignItems="center" css={{ paddingLeft: '$12' }}>
                  {icon}
                  <Text css={{ marginInline: '$4 $2' }} size="sm">
                    {field.label}
                  </Text>
                </Flex>
                {type === sidebarItems.EVENT_HANDLER ? (
                  <IconButton
                    css={{ justifyContent: 'center', opacity: 0 }}
                    icon={<DeleteIcon />}
                    size="sm"
                    color="light"
                    className="delete-btn"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!!removeEventFields) {
                        removeEventFields(field.label);
                      }
                    }}
                  />
                ) : null}
              </Flex>
            );
          })}
        </Flex>
      ) : (
        <>
          {isActive ? (
            <Text css={{ padding: '$4 $12', color: '$white500' }}>
              No events handlers found.
            </Text>
          ) : null}
        </>
      )}
    </Flex>
  );
};
